import ApiServer from "../ApiServer.ts";
import * as actionMenu from "../widget/action-menu-registry.ts";
import * as amplify from "../util/amplify.ts";
import * as CustomInput from "../util/CustomInput.ts";
import * as DateUtils from "../lib/DateUtils.ts";
import * as FwDatas from "../FwDatas.ts";
import * as Html from "../util/Html.ts";
import * as i18n from "../i18n.ts";
import * as INR from "../INR.ts";
import * as libLocation from "../lib/location.ts";
import * as libUrl from "../lib/url.ts";
import * as modal from "../util/modal.ts";
import { checkTaskLimitOntry, taskHardLimitReach } from "../list/list_clean.ts";
import { isFeatureEnabled } from "../lib/launchdarkly/launchdarkly.ts";
import { parseLeadingQuantity, PIECE } from "../lib/ingredientParser.ts";
import { quantityPicker } from "../widget/action-menu-content.ts";
import { formatQuantityUnit } from "../lib/unitFormat.ts";
import { MetaTaskCache, TaskListCache } from "../server/objects/TaskBeans.ts";
import { openListCreate } from "../util/taskutil.ts";
import { apiCustommplist, MealplannerSettingsCache } from "./mealplannerApi.ts";
import { type DishBean, type ITaskList, type MealSettingsBean, type RecipeBean, type RecipeIngredientBean, type ShoppingIngredientInputBean2, type TaskInputBean } from "../server/fizapilib.auto.ts";

import TPL_MODAL from "./modal_export.mustache";
import TPL_STEPS from "./modal_export_steps.mustache";
import TPL_DATES from "./modal_export_dates.mustache";
import TPL_LISTS from "./modal_export_lists.mustache";
import TPL_INGREDIENTS from "./modal_export_ingredients.mustache";
import TPL_QUANTITY from "./modal_export_quantity.mustache";
import * as segment from "../tracking-segment.ts";

/**
 * Export dishes and recipes to a shopping list, as a wizard: pick the dates, pick the destination
 * list, review what gets added. Three entry points, each opening it with what it already knows:
 * the planner settings menu (the displayed week, dates included), the export button of a meal slot
 * and a recipe of the recipe pane (both without the date step).
 */

type StepName = 'dates'|'list'|'ingredients';

/** end excluded, as everywhere in the planner */
type ExportRange = {start :Date, end :Date};

type ExportIngredient = {
  /** what the server matches in ShoppingIngredientInputBean2.ingredientList */
  metaId :string,
  name :string,
  /** re-parsed from the ingredient line when the recipe does not hold them apart */
  quantity :number,
  unit :string,
  /** the quantity as shown in the chip, empty when there is none */
  label :string,
  checked :boolean,
  dishIndex :number,
  index :number
};

type ExportDish = {
  metaId :string,
  name :string,
  /** the <img> of the recipe picture, when it has one */
  icon :string,
  subtitle :string,
  ingredients :ExportIngredient[],
  /** only meaningful without ingredients: such a dish is added under its own name */
  checked :boolean,
  /** how many of it, for a dish without ingredients: it is one line of the list, counted in pieces */
  quantity :number,
  unit :string,
  label :string,
  index :number
};

type Wizard = {
  steps :StepName[],
  step :number,
  /** only set when the dates can be changed, that is when the planner opened the wizard on a period */
  range ?:ExportRange,
  dishes :ExportDish[],
  lists :ITaskList[],
  listId :string,
  node :modal.DomModal
};

const STEP_TITLE :Record<StepName,string> = {
  dates:'mp.pickdate',
  list:'mp.picklist',
  ingredients:'mp.export.review'
};

var
  wizard :Wizard,
  /** the hint of the list step is dismissed for the whole session, not for a single wizard */
  bannerHidden = false;

/**
 * @param source either a period of the planner, which the user may then change, or the dishes and
 *   recipes to export, already known by the caller. listId preselects the destination list, the
 *   planner passes the one its pane displays.
 */
export async function openExportToList(source :{range ?:ExportRange, dishes ?:(DishBean|RecipeBean)[], listId ?:string}){
  const
    lists = await shoppingLists(),
    dishes = source.range
      ?await buildDishes((await apiCustommplist(source.range)).list)
      :await buildDishes(source.dishes||[]);

  wizard = {
    steps:source.range?['dates','list','ingredients']:['list','ingredients'],
    step:0,
    range:source.range && {start:source.range.start,end:source.range.end},
    dishes,
    lists,
    listId:lists.some(list=>list.metaId===source.listId)?source.listId:await defaultListId(lists),
    node:undefined
  };
  wizard.node = modal.openDomModal(TPL_MODAL);
  wizard.node.onClose = ()=>{wizard = undefined};
  renderStep();
}

/** Only groceries lists: they are the ones holding quantities, and the ones meals belong to. */
async function shoppingLists(){
  const [lists] = await MetaTaskCache.need();
  return lists.filter(list=>list.taskListType==='SHOPPING_LIST');
}

/** The list the family last exported to, then their default one, then the first groceries list. */
async function defaultListId(lists :ITaskList[]){
  const
    settings = await MealplannerSettingsCache.need(),
    preferred = lists.find(list=>list.metaId===settings.defaultAddMealToListId)
      || lists.find(list=>list.systemId===-10)
      || lists[0];

  return preferred?.metaId;
}

async function buildDishes(items :(DishBean|RecipeBean)[]) :Promise<ExportDish[]>{
  const settings = await MealplannerSettingsCache.need();

  return items.map(function(item,index){
    const
      isDish = item instanceof ApiServer.types.DishBean,
      recipe :RecipeBean = isDish?FwDatas.get((item as DishBean).recipeId):item as RecipeBean,
      dish :ExportDish = {
        metaId:item.metaId,
        name:item.name||recipe?.name,
        icon:item.icon,
        subtitle:isDish?dishSubtitle(item as DishBean,settings):'',
        ingredients:[],
        checked:true,
        //one of it by default, the stepper of its row lets the family ask for more
        quantity:1,
        unit:PIECE,
        label:quantityLabel(1,PIECE),
        index
      };

    dish.ingredients = (recipe?.ingredientsList||[]).map((ingredient,ingredientIndex)=>
      toExportIngredient(ingredient,index,ingredientIndex));

    return dish;
  });
}

/** "Breakfast - April 15", the meal the dish is planned for. */
function dishSubtitle(dish :DishBean, settings :MealSettingsBean){
  const
    column = settings.columns.find(one=>one.type===dish.type),
    date = new Date(dish.date+'T00:00');

  return [column?.displayName,DateUtils.toLocaleString(date,{month:'long',day:'numeric'})].filter(Boolean).join(' - ');
}

function toExportIngredient(ingredient :RecipeIngredientBean, dishIndex :number, index :number) :ExportIngredient{
  const written = ingredient.fullname();
  let
    name = written,
    quantity :number,
    unit :string;

  //without the quantity feature the line goes to the list as it is written in the recipe
  if(quantityActive()){
    quantity = parseFloat(String(ingredient.quantity).replace(',','.'));
    unit = ingredient.unit;
    //most recipes keep the whole line in the name: "100 g flour", "2 eggs"
    if(!(quantity>0)){
      const parsed = parseLeadingQuantity(written,i18n.getShortLanguage());
      if(parsed && parsed.name){
        name = parsed.name;
        quantity = parsed.quantity;
        unit = parsed.unit;
      }else{
        quantity = undefined;
      }
    }
  }

  return {
    metaId:ingredient.metaId,
    name,
    quantity,
    unit,
    label:quantityLabel(quantity,unit),
    checked:true,
    dishIndex,
    index
  };
}

/** Empty when there is no quantity: the chip then offers to add one. */
function quantityLabel(quantity :number, unit :string){
  return quantity>0?formatQuantityUnit(quantity,unit):'';
}

/** Quantities are still behind their flag: without it the ingredient lines are exported as written. */
function quantityActive(){
  return isFeatureEnabled('FWALL-33-list-item-quantity');
}

function ingredientAt(dishIndex :string, index :string){
  return wizard.dishes[Number(dishIndex)].ingredients[Number(index)];
}

function currentStep(){
  return wizard.steps[wizard.step];
}

/** How many lines the shopping list is about to get. */
function selectedCount(){
  return wizard.dishes.reduce((total,dish)=>total + (dish.ingredients.length
    ?dish.ingredients.filter(one=>one.checked).length
    :(dish.checked?1:0)),0);
}

/** A dish with ingredients is checked when all of them are: its check is a master check. */
function dishChecked(dish :ExportDish){
  return dish.ingredients.length?dish.ingredients.every(one=>one.checked):dish.checked;
}

function renderStep(){
  const
    node = wizard.node,
    step = currentStep(),
    body = node.querySelector<HTMLElement>('.mp-export-body');

  node.querySelector('.mp-export-title').textContent = i18n.get(STEP_TITLE[step]);
  Html.setHtml(node.querySelector<HTMLElement>('.mp-export-steps'),TPL_STEPS,wizard.steps.map((name,index)=>({
    number:index+1,
    done:index<wizard.step,
    current:index===wizard.step
  })));

  if(step==='dates'){
    Html.setHtml(body,TPL_DATES,{
      start:wizard.range.start,
      //the picked end day is included, the range is not
      end:DateUtils.dateAdd(wizard.range.end,{day:-1}),
      empty:!wizard.dishes.length
    });
    body.querySelectorAll('input[type=date]').forEach(input=>input.addEventListener('change',onDateChange));
  }else if(step==='list'){
    renderLists();
  }else{
    renderIngredients();
  }
  refreshFooter();
}

function renderLists(){
  Html.setHtml(wizard.node.querySelector<HTMLElement>('.mp-export-body'),TPL_LISTS,{
    rows:wizard.lists.map(list=>({
      list,
      selected:list.metaId===wizard.listId,
      count:i18n.get('mp.export.items',list.ongoing?.length||0)
    }))
  });
}

function renderIngredients(){
  const
    count = selectedCount(),
    editable = quantityActive(),
    body = wizard.node.querySelector<HTMLElement>('.mp-export-body');

  Html.setHtml(body,TPL_INGREDIENTS,{
    subtitle:i18n.get('mp.export.reviewexplain',{name:TaskListCache.get(wizard.listId)?.name}),
    //the banner is about the quantities that were filled in, it has nothing to say without them
    banner:!bannerHidden && editable,
    toggleLabel:i18n.get(count?'mp.export.deselectall':'mp.export.selectall'),
    selectedLabel:i18n.get('mp.export.selectedN',count),
    dishes:wizard.dishes.map(dish=>({
      ...dish,
      checked:dishChecked(dish),
      //a dish without ingredients is a line of its own, with its own quantity
      dishQuantity:editable && !dish.ingredients.length,
      //the flag travels with each line, the template reads it where the chip is written
      ingredients:dish.ingredients.map(one=>({...one,quantityActive:editable}))
    }))
  });
  body.querySelectorAll<HTMLInputElement>('.mp-export-name').forEach(function(input){
    const ingredient = ingredientAt(input.getAttribute('data-dish'),input.getAttribute('data-ingredient'));

    //typing is enough to rename, the step is redrawn on every check and would lose a pending value
    input.addEventListener('input',function(){
      if(this.value.trim()) ingredient.name = this.value.trim();
    });
    //a name cannot be emptied: the line would end up nameless in the list
    input.addEventListener('blur',function(){
      this.value = ingredient.name;
    });
  });
}

function refreshFooter(){
  const
    node = wizard.node,
    back = node.querySelector<HTMLButtonElement>('.mp-export-back'),
    next = node.querySelector<HTMLButtonElement>('.mp-export-next'),
    isLast = wizard.step===wizard.steps.length-1;

  back.style.display = wizard.step?'':'none';
  if(isLast){
    const count = selectedCount();
    next.textContent = i18n.get('mp.export.addN',count);
    next.disabled = count===0;
  }else{
    next.textContent = i18n.get('actions.next');
    next.disabled = currentStep()==='dates'?!wizard.dishes.length:!wizard.listId;
  }
}

/** The hint of the date step doubles as the warning telling the period holds no meal. */
function refreshHint(){
  const
    hint = wizard.node.querySelector<HTMLElement>('.mp-export-hint'),
    empty = !wizard.dishes.length;

  hint.classList.toggle('warn',empty);
  hint.textContent = i18n.get(empty?'mp.pickempty':'mp.pickdateexplain');
}

async function onDateChange(){
  const
    node = wizard.node,
    endInput = node.querySelector<HTMLInputElement>('input[name=end]'),
    start = CustomInput.getLocalDateValue(node.querySelector<HTMLInputElement>('input[name=start]')),
    end = CustomInput.getLocalDateValue(endInput);

  if(!start || !end) return;
  if(end<start){
    modal.oops('mp.pickdateinvalid');
    CustomInput.setDateValue(endInput,DateUtils.dateAdd(wizard.range.end,{day:-1}));
    return;
  }
  wizard.range = {start,end:DateUtils.dateAdd(end,{day:1})};
  wizard.dishes = await buildDishes((await apiCustommplist(wizard.range)).list);
  refreshHint();
  refreshFooter();
}

/** A dish or a recipe whose ingredients we know nothing about: the server adds what it has. */
function wholeDishBean(dish :ExportDish){
  const bean = new ApiServer.types.ShoppingIngredientInputBean2({dishMetaId:dish.metaId,ingredientList:[]});
  //null means "everything we know about it": its ingredients in DB, or its own name
  bean.ingredientList = null;
  return bean;
}

/** One reviewed line, the dish it comes from kept as its reference object. */
function itemBean(listId :string, dish :ExportDish, text :string, quantity :number, unit :string){
  return new ApiServer.types.TaskInputBean({
    taskListId:listId,
    refId:dish.metaId,
    text,
    quantity:quantity>0?String(quantity):undefined,
    unit:quantity>0?unit:undefined,
    recurrency:'NONE'
  });
}

async function submit(){
  const
    count = selectedCount(),
    editable = quantityActive(),
    listId = wizard.listId,
    list = TaskListCache.get(listId),
    beans :ShoppingIngredientInputBean2[] = [],
    items :TaskInputBean[] = [];

  if(!count) return;
  wizard.dishes.forEach(function(dish){
    //a dish holding no ingredient is a single line, added under its own name and its own quantity
    if(!dish.ingredients.length){
      if(!dish.checked) return;
      if(editable){
        items.push(itemBean(listId,dish,dish.name,dish.quantity,dish.unit));
      }else{
        beans.push(wholeDishBean(dish));
      }
      return;
    }
    const picked = dish.ingredients.filter(one=>one.checked);
    if(!picked.length) return;
    //the reviewed quantities cannot travel through the api of the meals, which only takes ids: such
    //ingredients are added as list items, keeping their dish as reference object
    if(editable){
      picked.forEach(one=>items.push(itemBean(listId,dish,one.name,one.quantity,one.unit)));
    }else{
      beans.push(new ApiServer.types.ShoppingIngredientInputBean2({
        dishMetaId:dish.metaId,
        //matched on their metaId, the ingredient goes to the list as the recipe stores it
        ingredientList:picked.map(one=>new ApiServer.types.RecipeIngredientBean({metaId:one.metaId}))
      }));
    }
  });
  if(!await checkTaskLimitOntry(count)){
    taskHardLimitReach(listId);
    return;
  }

  modal.closeDomModal(wizard.node);
  const calls :Promise<any>[] = items.map(item=>ApiServer.taskcreate2(item));
  if(beans.length){
    calls.push(ApiServer.mpadditemtolist2(beans,listId));
  }
  await Promise.all(calls);
  segment.track("Meal Exported To Tasks");
  //the planner shows the list the family just filled
  amplify.publish('mpexportlistpicked',list);
  amplify.publish('taskNeedRefresh');
  modal.toastDone('mp.export.done',{name:list?.name,count},{
    //straight to the groceries, where the added lines can still be edited
    onclick(){
      libLocation.goTo(libUrl.addParams(INR.URL.TASKS,{metaId:listId}));
    }
  });
}

actionMenu.register({
  /** The reviewed quantity, editable even when the ingredient line came without one. */
  mpexportquantity(){
    const
      node = this,
      ingredient = ingredientAt(node.getAttribute('data-dish'),node.getAttribute('data-ingredient')),
      content = quantityPicker({quantity:ingredient.quantity,unit:ingredient.unit});

    return {
      content,
      //the stepper would otherwise rewrite the chip on every click
      onCancel(){
        const {quantity,unit} = content.value();
        ingredient.quantity = quantity;
        ingredient.unit = unit;
        ingredient.label = quantityLabel(quantity,unit);
        //only the chip is redrawn: redrawing the step would pull the closing menu out of the dom
        node.classList.toggle('empty',!ingredient.label);
        Html.setHtml(node,TPL_QUANTITY,ingredient);
      }
    };
  },
  /** How many of a dish holding no ingredient: it is added as one line, counted in pieces. */
  mpexportdishquantity(){
    const
      node = this,
      dish = wizard.dishes[Number(node.getAttribute('data-dish'))],
      content = quantityPicker({quantity:dish.quantity,unit:dish.unit});

    return {
      content,
      onCancel(){
        const
          {quantity,unit} = content.value(),
          //a dish is one thing to buy, it cannot be asked for zero times
          asked = quantity>0?quantity:1,
          changed = asked!==dish.quantity || unit!==dish.unit;

        dish.quantity = asked;
        dish.unit = unit;
        dish.label = quantityLabel(asked,unit);
        Html.setHtml(node,TPL_QUANTITY,dish);
        //asking for a dish left out of the export means the family wants it after all
        if(changed && !dish.checked){
          dish.checked = true;
          //the step cannot be redrawn while the closing menu still hangs next to the chip
          setTimeout(function(){
            if(!wizard) return;
            renderIngredients();
            refreshFooter();
          },0);
        }
      }
    };
  }
});

Html.registerDom({
  mpexportnext(){
    if(!wizard) return;
    if(wizard.step===wizard.steps.length-1){
      submit();
      return;
    }
    wizard.step++;
    renderStep();
  },
  mpexportback(){
    if(!wizard || !wizard.step) return;
    wizard.step--;
    renderStep();
  },
  mpexportpicklist(metaId :string){
    wizard.listId = metaId;
    renderLists();
    refreshFooter();
  },
  async mpexportnewlist(){
    //the very modal of the lists page, minus its type: the export only fills groceries lists
    const created = await openListCreate('SHOPPING_LIST',{lockType:true});

    if(!created || !wizard) return;

    await MetaTaskCache.refresh();
    wizard.lists = await shoppingLists();
    wizard.listId = created.metaId;
    renderLists();
    refreshFooter();
  },
  mpexportbannerhide(){
    bannerHidden = true;
    renderIngredients();
  },
  mpexporttoggleall(){
    //nothing selected means the link offers to select everything back
    const checked = selectedCount()===0;

    wizard.dishes.forEach(dish=>{
      dish.checked = checked;
      dish.ingredients.forEach(one=>one.checked = checked);
    });
    renderIngredients();
    refreshFooter();
  },
  mpexportdish(index :string){
    const
      dish = wizard.dishes[Number(index)],
      checked = !dishChecked(dish);

    dish.checked = checked;
    dish.ingredients.forEach(one=>one.checked = checked);
    renderIngredients();
    refreshFooter();
  },
  mpexportingredient(dishIndex :string, index :string){
    const ingredient = wizard.dishes[Number(dishIndex)].ingredients[Number(index)];

    ingredient.checked = !ingredient.checked;
    renderIngredients();
    refreshFooter();
  }
});
